import { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LoadingIndicator from './LoadingIndicator'
import hero1 from '../assets/hero/hero1-optimized.avif'
import hero2 from '../assets/hero/hero2.avif'
import hero3 from '../assets/hero/hero3-optimized.avif'
import '../styles/glassmorphism.css'



const getHeroSlides = (t) => [
    {
        title: t('hero.slides.0.title'),
        subtitle: t('hero.slides.0.subtitle'),
        description: t('hero.slides.0.description'),
        image: hero1
    },
    {
        title: t('hero.slides.1.title'),
        subtitle: t('hero.slides.1.subtitle'),
        description: t('hero.slides.1.description'),
        image: hero2
    },
    {
        title: t('hero.slides.2.title'),
        subtitle: t('hero.slides.2.subtitle'),
        description: t('hero.slides.2.description'),
        image: hero3
    }
]


const Hero = () => {
    const { t, i18n } = useTranslation()
    const [currentTextIndex, setCurrentTextIndex] = useState(0)
    const [imagesLoaded, setImagesLoaded] = useState({})
    const [loadedCount, setLoadedCount] = useState(0)
    const navigate = useNavigate()
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku'

    const heroTexts = getHeroSlides(t)

    const handleImageLoad = (index) => {
        setImagesLoaded(prev => ({ ...prev, [index]: true }))
        setLoadedCount(prev => prev + 1)
    }

    // Auto-advance carousel
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length)
        }, 6000)
        return () => clearInterval(interval)
    }, [heroTexts.length])


    const containerRef = useRef(null)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    })

    return (
        <section ref={containerRef} className="relative h-[100dvh] overflow-hidden">
            {loadedCount < 3 && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-black">
                    <LoadingIndicator size="large" showText={true} />
                </div>
            )}

            <div className="absolute inset-0">
                {heroTexts.map((slide, index) => (
                    <motion.img
                        key={index}
                        src={[hero1, hero2, hero3][index]}
                        alt={slide.title}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        style={{ display: imagesLoaded[index] ? 'block' : 'none' }}
                        initial={{ opacity: index === 0 ? 1 : 0 }}
                        animate={{ opacity: index === currentTextIndex ? 1 : 0 }}
                        transition={{ duration: 1 }}
                        onLoad={() => handleImageLoad(index)}
                    />
                ))}

                {/* Image overlays */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </div>

            {/* Main Content: Set to flex-col and space-between to push content to top/bottom on large screens, 
                but allow centralization on small screens. */}
            <div className="relative z-20 h-full flex flex-col justify-start sm:justify-between">

                {/* Top/Text Content Container: Pushed down for visual balance on large screens */}
                <div className="flex-1 flex items-center sm:items-start pt-12 xs:pt-12 min-[390px]:pt-0 min-[390px]:-mt-20 min-[430px]:pt-0 min-[430px]:-mt-24 sm:pt-28 sm:mt-0 md:pt-32 lg:pt-36">
                    <div className="w-full px-4 xs:px-5 sm:px-8 lg:px-12 max-w-7xl mx-auto">
                        <motion.div
                            key={currentTextIndex}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -30 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="space-y-4 xs:space-y-5 sm:space-y-6 md:space-y-8 w-full"
                        >
                            {/* Text Content */}
                            <div className="space-y-3 xs:space-y-3 sm:space-y-4">
                                <div className="text-white/80 text-xs xs:text-sm sm:text-base font-semibold tracking-[0.15em] uppercase">
                                    {heroTexts[currentTextIndex].subtitle}
                                </div>
                                <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-medium text-white leading-[0.9] xs:leading-[0.95] tracking-tight">
                                    {heroTexts[currentTextIndex].title}
                                </h1>
                            </div>

                            <p className="text-sm xs:text-base sm:text-lg md:text-xl text-white/90 font-normal leading-relaxed max-w-2xl lg:max-w-3xl">
                                {heroTexts[currentTextIndex].description}
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Bottom Content Container (CTA + Stats) */}
                <div className="pb-12 xs:pb-14 sm:pb-16 lg:pb-20 mt-16 sm:mt-0"> {/* Added mt-16 for separation on small screens */}
                    <div className="w-full px-4 xs:px-5 sm:px-8 lg:px-12 max-w-7xl mx-auto">
                        <motion.div
                            key={currentTextIndex}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -30 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                            className="space-y-5 xs:space-y-6 sm:space-y-6"
                        >
                            {/* CTA Button */}
                            <div>
                                <button
                                    onClick={() => navigate('/services')}
                                    className="group bg-white/5 backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)] text-white px-6 py-3 xs:px-7 xs:py-3.5 sm:px-8 sm:py-4 rounded-full font-medium hover:bg-white/10 transition-all duration-300 flex items-center gap-2 xs:gap-3 justify-center text-sm xs:text-base sm:text-base min-h-[48px] xs:min-h-[52px] sm:min-h-[56px] touch-manipulation w-full xs:w-auto"
                                >
                                    {isRTL && <ArrowLeft className="w-4 h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform duration-300" />}
                                    {t('hero.cta')}
                                    {!isRTL && <ArrowRight className="w-4 h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />}
                                </button>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4 xs:gap-5 sm:gap-8 lg:gap-12 max-w-2xl lg:max-w-3xl">
                                <div className="text-center">
                                    <div className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-light text-white mb-1 xs:mb-2">150+</div>
                                    <div className="text-white/70 text-xs xs:text-sm sm:text-base font-medium tracking-wide leading-tight">{t('hero.stats.projects')}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-light text-white mb-1 xs:mb-2">25+</div>
                                    <div className="text-white/70 text-xs xs:text-sm sm:text-base font-medium tracking-wide leading-tight">{t('hero.stats.experience')}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-light text-white mb-1 xs:mb-2">12</div>
                                    <div className="text-white/70 text-xs xs:text-sm sm:text-base font-medium tracking-wide leading-tight">{t('hero.stats.countries')}</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Progress Indicators: Hidden on mobile (default) and visible from sm: (tablet/desktop) */}
            <div className="absolute bottom-6 sm:bottom-8 lg:bottom-10 left-6 sm:left-8 lg:left-12 z-30 hidden sm:flex gap-2 sm:gap-3">
                {heroTexts.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentTextIndex(index)}
                        className={`h-0.5 transition-all duration-300 touch-manipulation backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)] ${index === currentTextIndex
                            ? 'bg-white/90 w-8 sm:w-12 lg:w-16'
                            : 'bg-white/20 w-4 sm:w-8 lg:w-10 hover:bg-white/40'
                            }`}
                    />
                ))}
            </div>


        </section>
    )
}

export default Hero
